import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useScreenShareStore } from '@/store/screen-share.store';
import styles from './screen-share-viewer.module.scss';

interface ScreenShareViewerProps {
  conversationId: number;
}

// Renders the visitor's live screen share for a given conversation.
// Mounts only when there's an active entry in the screen-share store
// (i.e. the visitor pressed Share and the CRM forwarded the first
// `screen_started` event for this conversation). Internally:
//   1. Creates a MediaSource and binds it to the video element via
//      URL.createObjectURL.
//   2. On `sourceopen`, adds a SourceBuffer with the mime advertised
//      in `screen_started`.
//   3. Drains the per-conversation `pending` queue on every store
//      update, appending each Uint8Array chunk to the SourceBuffer.
//   4. On `screen_ended`, calls endOfStream() so the video freezes on
//      the final frame instead of looping.
// On unmount the SourceBuffer is detached and the store entry is
// cleared so a future re-mount doesn't replay stale chunks.
export const ScreenShareViewer = ({ conversationId }: ScreenShareViewerProps) => {
  const { t } = useTranslation('webchat');
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // Refs let the effect mutate without driving renders; React state
  // would be a re-render storm at 5 fps × chunks-per-frame.
  const mediaSourceRef = useRef<MediaSource | null>(null);
  const sourceBufferRef = useRef<SourceBuffer | null>(null);
  // Holds chunks that arrived before the SourceBuffer was ready
  // (MediaSource transitions through "closed" → "open" asynchronously
  // after the video element gains a src). Drained in `sourceopen`.
  const preOpenQueueRef = useRef<Uint8Array[]>([]);
  // Append calls are async — kick off the next chunk only when the
  // SourceBuffer fires `updateend`. Without this guard appendBuffer
  // throws InvalidStateError when called during an active update.
  const appendInFlightRef = useRef(false);

  const entry = useScreenShareStore((s) => s.shares[conversationId]);
  const drain = useScreenShareStore((s) => s.drain);
  const clear = useScreenShareStore((s) => s.clear);

  // (re)create the MediaSource whenever the mime changes (a fresh
  // share session means a fresh SourceBuffer).
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !entry) return;

    const mediaSource = new MediaSource();
    mediaSourceRef.current = mediaSource;
    const objectUrl = URL.createObjectURL(mediaSource);
    video.src = objectUrl;

    const setup = () => {
      // Sanity: confirm the requested mime is supported by this
      // browser's Media Source Extensions before attempting to add a
      // SourceBuffer (otherwise it throws NotSupportedError silently
      // when the catch swallows it).
      if (!('MediaSource' in window) || !MediaSource.isTypeSupported(entry.mime)) {
        console.error(`ScreenShareViewer: MIME not supported: ${entry.mime}`);
        return;
      }
      try {
        const sourceBuffer = mediaSource.addSourceBuffer(entry.mime);
        // Decode time grows fast with live capture; tell the buffer to
        // operate in "sequence" mode so each appendBuffer's timestamps
        // are stitched onto the previous one without gaps.
        sourceBuffer.mode = 'sequence';
        sourceBufferRef.current = sourceBuffer;
        sourceBuffer.addEventListener('updateend', () => {
          appendInFlightRef.current = false;
          // Kick the video element if it has frames but is paused —
          // autoplay can fail silently when MediaSource attaches.
          const v = videoRef.current;
          if (v && v.paused && v.readyState >= 2) {
            v.play().catch(() => {});
          }
          flushPreOpenQueue();
        });
        sourceBuffer.addEventListener('error', (e) => {
          console.error('ScreenShareViewer: sourceBuffer error', e);
        });
        flushPreOpenQueue();
      } catch (err) {
        console.error('ScreenShareViewer: addSourceBuffer failed', err);
      }
    };
    const flushPreOpenQueue = () => {
      const sb = sourceBufferRef.current;
      if (!sb || appendInFlightRef.current) return;
      const next = preOpenQueueRef.current.shift();
      if (!next) return;
      appendInFlightRef.current = true;
      try {
        // ts-lib's BufferSource union doesn't accept Uint8Array<ArrayBufferLike>
        // even though the runtime is fine; pass the underlying buffer slice.
        sb.appendBuffer(next as unknown as BufferSource);
      } catch (err) {
        appendInFlightRef.current = false;
        console.error('ScreenShareViewer: appendBuffer failed', err);
      }
    };
    mediaSource.addEventListener('sourceopen', setup);

    return () => {
      mediaSource.removeEventListener('sourceopen', setup);
      try {
        if (mediaSource.readyState === 'open') mediaSource.endOfStream();
      } catch {
        // Ignore InvalidStateError from racing endOfStream and unmount.
      }
      URL.revokeObjectURL(objectUrl);
      mediaSourceRef.current = null;
      sourceBufferRef.current = null;
      preOpenQueueRef.current = [];
      appendInFlightRef.current = false;
    };
  }, [entry?.mime, conversationId]);

  // Drain pending chunks from the store into the SourceBuffer (or the
  // pre-open queue if the buffer isn't ready yet).
  useEffect(() => {
    if (!entry || entry.pending.length === 0) return;
    preOpenQueueRef.current.push(...entry.pending);
    drain(conversationId, entry.pending.length);
    const sb = sourceBufferRef.current;
    if (!sb || appendInFlightRef.current) return;
    const next = preOpenQueueRef.current.shift();
    if (!next) return;
    appendInFlightRef.current = true;
    try {
      sb.appendBuffer(next as unknown as BufferSource);
    } catch (err) {
      appendInFlightRef.current = false;
      console.error('ScreenShareViewer: appendBuffer failed', err);
    }
  }, [entry, drain, conversationId]);

  // When the share ends, close the MediaSource so the video freezes
  // on the last decoded frame instead of buffering forever.
  useEffect(() => {
    if (!entry?.ended) return;
    const ms = mediaSourceRef.current;
    if (ms && ms.readyState === 'open' && !appendInFlightRef.current) {
      try {
        ms.endOfStream();
      } catch {
        // ignore — already ended or transitioned.
      }
    }
  }, [entry?.ended]);

  // NOTE: we deliberately don't clear the store entry on unmount.
  // React strict mode double-invokes effects in development which
  // wipes the entry that was *just* created when screen_started
  // arrived, causing the viewer to flicker off immediately. Server
  // drives entry lifecycle via screen_started / screen_ended; the
  // ephemeral nature of the store (no persistence) means a new
  // conversation always starts with a fresh entry anyway.
  void clear;

  if (!entry) return null;

  return (
    <div className={styles.screenShareViewer} data-testid="screen-share-viewer">
      <div className={styles.header}>
        <span className={styles.dot} aria-hidden="true">
          🟢
        </span>
        <span>{entry.ended ? t('screenShare.titleEnded') : t('screenShare.title')}</span>
      </div>
      <video
        ref={videoRef}
        className={styles.video}
        autoPlay
        muted
        playsInline
        controls={false}
      />
    </div>
  );
};
